import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, of, Subscription } from 'rxjs';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CharId, FtthResourceService, Resource, ResSpecificationId } from '../../../../services/api/ftth-resource/ftth-resource.service';
import { MatPaginator, MatSort, PageEvent, Sort } from '@angular/material';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { BreadcrumbsService } from '../../../../services/utils/breadcrumbs.service';
import { ActivatedRoute, Router } from '@angular/router';

interface SearchObject {
  resSpecificationId: ResSpecificationId | '';
  charId: CharId;
  charValue: string;
}

@Component({
  selector: 'app-search-resources',
  templateUrl: './search-ftth-resources.component.html',
  styleUrls: ['./search-ftth-resources.component.scss']
})
export class SearchFtthResourcesComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  displayedColumns: string[] = ['name', 'type', 'lifecycleState', 'capacity', 'occupancy'];
  data: Resource[] = [];
  resultsLength = 0;

  loading = false;

  searchGroup: FormGroup;

  private searchSubject = new BehaviorSubject<void>(null);

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private ftthResourceService: FtthResourceService,
    private breadcrumbsService: BreadcrumbsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit() {
    this.searchGroup = this.formBuilder.group({
      resSpecificationId: this.formBuilder.control(''),
      charId: this.formBuilder.control('name'),
      charValue: this.formBuilder.control(''),
    });

    this.breadcrumbsService.sendBreadcrumbs(
      {
        breadcrumbs:
          [
            { title: 'Recursos', url: 'provision/resources' },
            { title: 'Recursos FTTH', url: 'provision/resources/ftth/search' }
          ]
      }
    );
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      combineLatest(
        this.searchGroup.valueChanges
          .pipe(
            startWith(this.searchGroup.value),
            debounceTime(500),
            // tslint:disable-next-line:max-line-length
            distinctUntilChanged((previous: SearchObject, current: SearchObject) => previous.resSpecificationId === current.resSpecificationId && previous.charId === current.charId && previous.charValue === current.charValue),
            // tslint:disable-next-line:max-line-length
            filter((search: SearchObject) => !(search.resSpecificationId && search.charId && search.charId !== 'name' && !search.charValue)),
            tap(() => this.paginator.firstPage()),
          ),
        this.sort.sortChange
          .pipe(
            startWith({
              active: this.sort.active,
              direction: this.sort.direction
            } as Sort),
            tap(() => this.paginator.firstPage()),
          ),
        this.paginator.page
          .pipe(
            startWith({
              pageIndex: this.paginator.pageIndex,
              previousPageIndex: 0,
              pageSize: this.paginator.pageSize,
              length: this.paginator.length
            } as PageEvent),
          ),
        this.searchSubject.asObservable(),
      )
        .pipe(
          debounceTime(50),
          switchMap(([search, sort, page]: [SearchObject, Sort, PageEvent, void]) => {
            this.loading = true;
            return this.ftthResourceService
              .getResources(
                search.resSpecificationId,
                search.charId,
                search.charValue,
                page.pageIndex,
                page.pageSize,
                sort.active,
                sort.direction)
              .pipe(
                catchError(() => of({
                  page: {
                    size: 0,
                    totalElements: 0,
                    totalPages: 0,
                    number: 0,
                  },
                  resources: []
                })),
              );
          }),
          map(data => {
            this.loading = false;
            this.resultsLength = data.page.totalElements;
            return data.resources;
          }),
          catchError(() => {
            this.loading = false;
            this.resultsLength = 0;
            return EMPTY;
          }),
        )
        .subscribe(data => this.data = data));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  resetForm() {
    this.resSpecificationIdControl.setValue('');
    this.charIdControl.setValue('name');
    this.charValueControl.setValue('');
  }

  search() {
    this.searchSubject.next();
  }

  resSpecificationIdChanged() {
    this.charIdControl.setValue('name');
    this.charValueControl.setValue('');
  }

  navigateToDetail(row: Resource) {
    const hrefLinkChunks = row._links.self.href.split('/');

    this.router.navigate(['..', 'detail', btoa(hrefLinkChunks[hrefLinkChunks.length - 1])], { relativeTo: this.activatedRoute });
    //this.router.navigate(['..', 'detail', btoa(row.name)], { relativeTo: this.activatedRoute });
  }

  get resSpecificationIdControl(): FormControl {
    return this.searchGroup.get('resSpecificationId') as FormControl;
  }

  get charIdControl(): FormControl {
    return this.searchGroup.get('charId') as FormControl;
  }

  get charValueControl(): FormControl {
    return this.searchGroup.get('charValue') as FormControl;
  }
}
